import { useMemo } from 'react'
import { Button, Label, Skeleton } from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import { useAIAgentList, useAITagList } from '@/services'

import { KaiConfigsProps } from './kai-configs.type'

import styles from './kai-configs.module.css'

export const KaiConfigs = ({
  selectedAgent,
  onHandleSelectAgent,
  onHandleSelectTags,
  selectedTags,
}: KaiConfigsProps) => {
  const { t } = useTranslation('screenAiCenter')

  const { data, isLoading: isLoadingAgents } = useAIAgentList({
    params: {
      limit: 100,
      offset: 0,
    },
  })

  const { data: dataTags, isLoading: isLoadingTags } = useAITagList({
    params: {
      limit: 100,
      offset: 0,
    },
  })

  const onChangeTags = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onHandleSelectTags(selectedTags.filter((t) => t !== tag))
    } else {
      onHandleSelectTags([...selectedTags, tag])
    }
  }

  const activateAgents = useMemo(() => {
    if (isLoadingAgents) {
      return (
        <div className={styles['agents-container']}>
          <Skeleton
            height="2rem"
            width="5rem"
            className={styles['agent-skeleton']}
          />
          <Skeleton
            height="2rem"
            width="5rem"
            className={styles['agent-skeleton']}
          />
          <Skeleton
            height="2rem"
            width="5rem"
            className={styles['agent-skeleton']}
          />
        </div>
      )
    }

    const filteredAgents = data?.data?.results?.filter(
      (agent) =>
        agent.active &&
        agent.type === 'flow' &&
        ['customer_in', 'customer_out', 'customer_share'].includes(agent.role),
    )

    const validAgents = filteredAgents?.reduce(
      (uniqueAgents, currentAgent) => {
        const existingAgent = uniqueAgents.find(
          (agent) => agent.name === currentAgent.name,
        )
        if (!existingAgent) {
          uniqueAgents.push(currentAgent)
        }
        return uniqueAgents
      },
      [] as typeof filteredAgents,
    )

    return (
      <div className={styles['agents-container']}>
        {validAgents?.map((agent) => (
          <Button
            model="pill"
            size="small"
            isActive={selectedAgent === agent.name}
            key={agent.name}
            variant="secondary"
            onClick={() => onHandleSelectAgent(agent.name)}
          >
            {agent.name}
          </Button>
        ))}
      </div>
    )
  }, [data?.data, isLoadingAgents, selectedAgent])

  const activateTags = useMemo(() => {
    if (isLoadingTags) {
      return (
        <Skeleton
          height="2rem"
          width="5rem"
          className={styles['tag-skeleton']}
        />
      )
    }

    return (
      <div className={styles['tags-container']}>
        {dataTags?.data?.results?.map((tag) => (
          <Button
            key={tag.uuid}
            isActive={selectedTags.includes(tag.slug)}
            size="small"
            variant="secondary"
            model="pill"
            onClick={() => onChangeTags(tag.slug)}
          >
            {tag.slug}

            <div
              className={styles['tag-color']}
              style={{
                backgroundColor: tag.color,
              }}
            />
          </Button>
        ))}
      </div>
    )
  }, [dataTags?.data, isLoadingTags, selectedTags])

  return (
    <div className={styles.core}>
      <div>
        <Label>{t('kaiConfigsActivateAgentsLabel')}</Label>
        {activateAgents}
      </div>
      <div>
        <Label>{t('kaiConfigsActivateTagsLabel')}</Label>
        {activateTags}
      </div>
    </div>
  )
}
